import {FormEvent, ReactNode, useEffect, useId, useState} from "react";
import {Alert, Badge, Button, Card, Form, Modal} from "react-bootstrap";
import QRCode from "qrcode";
import {ProfileApi} from "../../api/ProfileApi";
import {MfaStatus, TotpSetup, UserViewModel} from "../../api/models";
import {CopyButton} from "../../components/CopyButton";
import {Field, FieldControl} from "../../components/Field";
import {ErrorAlert} from "../../components/ErrorAlert";
import {SubmitButton} from "../../components/SubmitButton";
import {account, common, format, rich} from "../../i18n";

type PasswordAction = 'enable-totp' | 'disable-totp' | 'enable-email' | 'disable-email' | 'regenerate';

const passwordActionTitles: Record<PasswordAction, string> = account.twoFactor.actions;

const passwordErrors = {
    400: account.twoFactor.passwordIncorrect,
    409: account.twoFactor.alreadyDone,
    429: account.twoFactor.tooManyAttempts
};

const codeErrors = {
    400: account.twoFactor.codeIncorrect,
    429: account.twoFactor.tooManyAttempts
};

// Opt-in second factors. Either or both can be on; once one is, signing in needs a code.
export function TwoFactorCard({profile, onChanged}: { profile: UserViewModel, onChanged(mfa: MfaStatus): void }) {
    const mfa = profile.mfa;
    const [action, setAction] = useState<PasswordAction>();
    const [totpSetup, setTotpSetup] = useState<TotpSetup>();
    const [confirmingEmail, setConfirmingEmail] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>();
    const [notice, setNotice] = useState<string>();

    // Runs the chosen action once the password is confirmed.
    const runWithPassword = async (password: string) => {
        switch (action) {
            case 'enable-totp':
                setTotpSetup(await ProfileApi.beginTotpSetup(password));
                break;
            case 'disable-totp':
                await ProfileApi.disableTotp(password);
                onChanged({ ...mfa, totpEnabled: false, recoveryCodesRemaining: mfa.emailEnabled ? mfa.recoveryCodesRemaining : 0 });
                setNotice(account.twoFactor.notices.totpRemoved);
                break;
            case 'enable-email':
                await ProfileApi.sendEmailMfaCode(password);
                setConfirmingEmail(true);
                break;
            case 'disable-email':
                await ProfileApi.disableEmailMfa(password);
                onChanged({ ...mfa, emailEnabled: false, recoveryCodesRemaining: mfa.totpEnabled ? mfa.recoveryCodesRemaining : 0 });
                setNotice(account.twoFactor.notices.emailOff);
                break;
            case 'regenerate': {
                const result = await ProfileApi.regenerateRecoveryCodes(password);
                onChanged({ ...mfa, recoveryCodesRemaining: result.recoveryCodes.length });
                setRecoveryCodes(result.recoveryCodes);
                break;
            }
        }
        setAction(undefined);
    };

    const enabled = mfa.totpEnabled || mfa.emailEnabled;
    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Title className="mb-3">{account.twoFactor.title}</Card.Title>
                <p className="text-body-secondary">{account.twoFactor.intro}</p>
                {notice && <Alert variant="success" dismissible onClose={() => setNotice(undefined)}>{notice}</Alert>}

                <MethodRow title={account.twoFactor.totp.title} description={account.twoFactor.totp.description}
                           enabled={mfa.totpEnabled}
                           onEnable={() => setAction('enable-totp')} onDisable={() => setAction('disable-totp')}/>
                <MethodRow title={account.twoFactor.email.title} description={rich(account.twoFactor.email.description, { strong: text => <strong>{text}</strong> }, { email: profile.emailAddress })}
                           enabled={mfa.emailEnabled}
                           onEnable={() => setAction('enable-email')} onDisable={() => setAction('disable-email')}/>
                {enabled && (
                    <div className="d-flex justify-content-between align-items-start gap-3 pt-3">
                        <div>
                            <div className="fw-semibold">{account.twoFactor.recoveryCodes}</div>
                            <div className="small text-body-secondary">
                                {format(account.twoFactor.recoveryCodesRemaining, { count: mfa.recoveryCodesRemaining })}
                            </div>
                        </div>
                        <Button size="sm" variant="outline-secondary" className="text-nowrap" onClick={() => setAction('regenerate')}>
                            {mfa.recoveryCodesRemaining ? account.twoFactor.regenerate : account.twoFactor.generate}
                        </Button>
                    </div>
                )}
            </Card.Body>

            <PasswordModal action={action} onSubmit={runWithPassword} onHide={() => setAction(undefined)}/>
            {totpSetup && (
                <TotpSetupModal setup={totpSetup} onHide={() => setTotpSetup(undefined)} onConfirmed={codes => {
                    setTotpSetup(undefined);
                    onChanged({ ...mfa, totpEnabled: true, recoveryCodesRemaining: codes?.length ?? mfa.recoveryCodesRemaining });
                    setNotice(account.twoFactor.notices.totpSetUp);
                    setRecoveryCodes(codes);
                }}/>
            )}
            {confirmingEmail && (
                <EmailCodeModal emailAddress={profile.emailAddress} onHide={() => setConfirmingEmail(false)} onConfirmed={() => {
                    setConfirmingEmail(false);
                    onChanged({ ...mfa, emailEnabled: true });
                    setNotice(account.twoFactor.notices.emailOn);
                }}/>
            )}
            {recoveryCodes && <RecoveryCodesModal codes={recoveryCodes} onHide={() => setRecoveryCodes(undefined)}/>}
        </Card>
    );
}

function MethodRow({title, description, enabled, onEnable, onDisable}: {
    title: string, description: ReactNode, enabled: boolean, onEnable(): void, onDisable(): void
}) {
    return (
        <div className="d-flex justify-content-between align-items-start gap-3 py-3 border-bottom">
            <div>
                <div className="fw-semibold">
                    {title} {enabled ? <Badge bg="success" className="ms-1">{account.twoFactor.on}</Badge> : <Badge bg="secondary" className="ms-1">{account.twoFactor.off}</Badge>}
                </div>
                <div className="small text-body-secondary">{description}</div>
            </div>
            {enabled
                ? <Button size="sm" variant="outline-danger" className="text-nowrap" onClick={onDisable}>{account.twoFactor.turnOff}</Button>
                : <Button size="sm" variant="outline-primary" className="text-nowrap" onClick={onEnable}>{account.twoFactor.turnOn}</Button>}
        </div>
    );
}

function PasswordModal({action, onSubmit, onHide}: { action?: PasswordAction, onSubmit(password: string): Promise<void>, onHide(): void }) {
    const titleId = useId();
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    useEffect(() => {
        setPassword('');
        setError(undefined);
    }, [action]);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(undefined);
        try {
            await onSubmit(password);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show={!!action} onHide={onHide} centered>
            <Form onSubmit={submit}>
                <Modal.Header closeButton>
                    <Modal.Title id={titleId}>{action && passwordActionTitles[action]}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} messages={passwordErrors}/>
                    <Form.Group controlId="mfa-password">
                        <Form.Label>{account.twoFactor.confirmPassword}</Form.Label>
                        <Form.Control type="password" autoComplete="current-password" autoFocus required
                                      value={password} onChange={e => setPassword(e.target.value)}/>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>{common.cancel}</Button>
                    <SubmitButton busy={busy} variant={action?.startsWith('disable') ? 'danger' : 'primary'}>{account.twoFactor.continue}</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

// Groups of four, as authenticator apps show secrets for typing in.
const formatSecret = (secret: string) => secret.match(/.{1,4}/g)?.join(' ') ?? secret;

function TotpSetupModal({setup, onHide, onConfirmed}: { setup: TotpSetup, onHide(): void, onConfirmed(recoveryCodes?: string[]): void }) {
    const titleId = useId();
    const [qr, setQr] = useState<string>();
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    useEffect(() => {
        QRCode.toDataURL(setup.otpauthUri, { margin: 1, width: 200 }).then(setQr, () => setQr(undefined));
    }, [setup.otpauthUri]);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(undefined);
        try {
            const result = await ProfileApi.confirmTotpSetup(code.trim());
            onConfirmed(result?.recoveryCodes);
        } catch (err) {
            setError(err);
            setBusy(false);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show onHide={onHide} centered backdrop="static">
            <Form onSubmit={submit}>
                <Modal.Header closeButton>
                    <Modal.Title id={titleId}>{account.twoFactor.totpSetup.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ol className="ps-3">
                        <li className="mb-2">{account.twoFactor.totpSetup.scan}</li>
                        <li className="mb-2">{account.twoFactor.totpSetup.enterCode}</li>
                    </ol>
                    <div className="text-center mb-3">
                        {qr && <img src={qr} alt={account.twoFactor.totpSetup.qrAlt} width={200} height={200} className="bg-white p-2 rounded"/>}
                    </div>
                    <p className="small text-body-secondary mb-1">{account.twoFactor.totpSetup.cantScan}</p>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <code className="fs-6 text-break">{formatSecret(setup.secret)}</code>
                        <CopyButton text={setup.secret} label={account.twoFactor.totpSetup.copyKey}/>
                    </div>
                    <ErrorAlert error={error} messages={codeErrors}/>
                    <Form.Group controlId="totp-code">
                        <Form.Label>{account.twoFactor.code}</Form.Label>
                        <Form.Control autoFocus required autoComplete="one-time-code" inputMode="numeric" maxLength={6}
                                      className="font-monospace" value={code} onChange={e => setCode(e.target.value)}/>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>{common.cancel}</Button>
                    <SubmitButton busy={busy}>{account.twoFactor.turnOn}</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

function EmailCodeModal({emailAddress, onHide, onConfirmed}: { emailAddress: string, onHide(): void, onConfirmed(): void }) {
    const titleId = useId();
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(undefined);
        try {
            await ProfileApi.confirmEmailMfa(code.trim());
            onConfirmed();
        } catch (err) {
            setError(err);
            setBusy(false);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show onHide={onHide} centered backdrop="static">
            <Form onSubmit={submit}>
                <Modal.Header closeButton>
                    <Modal.Title id={titleId}>{account.twoFactor.emailSetup.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{rich(account.twoFactor.emailSetup.sent, { strong: text => <strong>{text}</strong> }, { email: emailAddress })}</p>
                    <ErrorAlert error={error} messages={codeErrors}/>
                    <Field controlId="email-mfa-code" label={account.twoFactor.code} help={account.twoFactor.emailSetup.help}>
                        <FieldControl autoFocus required autoComplete="one-time-code" inputMode="numeric" maxLength={6}
                                      className="font-monospace" value={code} onChange={e => setCode(e.target.value)}/>
                    </Field>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>{common.cancel}</Button>
                    <SubmitButton busy={busy}>{account.twoFactor.turnOn}</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

// Shown once: the API stores only hashes.
function RecoveryCodesModal({codes, onHide}: { codes: string[], onHide(): void }) {
    const titleId = useId();
    const text = codes.join('\n');

    const download = () => {
        const url = URL.createObjectURL(new Blob([`${account.twoFactor.savedCodes.fileHeading}\n\n${text}\n`], { type: 'text/plain' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = account.twoFactor.savedCodes.fileName;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Modal aria-labelledby={titleId} show onHide={onHide} centered backdrop="static">
            <Modal.Header>
                <Modal.Title id={titleId}>{account.twoFactor.savedCodes.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{rich(account.twoFactor.savedCodes.text, { strong: text => <strong>{text}</strong> })}</p>
                <div className="row row-cols-2 g-2 font-monospace text-center mb-3">
                    {codes.map(x => <div key={x} className="col"><div className="border rounded py-1">{x}</div></div>)}
                </div>
                <div className="d-flex gap-2">
                    <CopyButton text={text} label={account.twoFactor.savedCodes.copy}/>
                    <Button size="sm" variant="outline-secondary" onClick={download}><i className="bi bi-download me-1"></i>{account.twoFactor.savedCodes.download}</Button>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onHide}>{account.twoFactor.savedCodes.done}</Button>
            </Modal.Footer>
        </Modal>
    );
}
